<template>
  <div class="Home">
    <Navbar :language="language" @changeLanguage="changeLanguage"/>
  </div>

  <div class="bg-gray-900">
    <div>
      <div class="relative pt-10 animate-delay-[500ms] animate-fade-left">
        <!-- Vídeo Hero dinâmico com fallback -->
        <!-- Desktop -->
        <video v-if="pageConfig?.heroMedia" autoplay loop muted playsinline
               class="absolute bg-gray-900 inset-0 h-full w-full object-cover hidden md:block">
          <source :src="pageConfig.heroMedia" type="video/mp4">
        </video>
        <video v-else autoplay loop muted playsinline
               class="absolute bg-gray-900 inset-0 h-full w-full object-cover hidden md:block">
          <source :src="require('@/assets/trailercapav2.mp4')" type="video/mp4">
        </video>
        <!-- Mobile -->
        <video v-if="pageConfig?.heroMediaMobile" autoplay loop muted playsinline
               class="absolute bg-gray-900 inset-0 h-full w-full object-cover block md:hidden">
          <source :src="pageConfig.heroMediaMobile" type="video/mp4">
        </video>
        <video v-else autoplay loop muted playsinline
               class="absolute bg-gray-900 inset-0 h-full w-full object-cover block md:hidden">
          <source :src="require('@/assets/trailermobile.mp4')" type="video/mp4">
        </video>
        <div class="bg-gradient-to-t relative bg-gray-900 bg-opacity-10 from-gray-900 from-1% to-transparent">
          <div
              class="relative mx-auto overflow-hidden flex flex-col items-center justify-center px-4 py-16 sm:max-w-xl md:max-w-full h-[650px] md:px-24 lg:max-w-screen-xl lg:px-8 lg:py-20">
            <div class="text-center mx-5 pt-10 lg:w-2/4 w-3/4 flex flex-col items-center">
              <h2 class="font-sans text-4xl font-roboto font-bold tracking-tight text-white md:text-7xl">
                {{ pageConfig?.welcomeMessage || "" }}
              </h2>
              <div class="flex justify-center items-center pt-12">
                <img @click.native="navigateToSection('gamesSection')" src="@/assets/seta.svg"
                     class="lg:w-16 w-14 text-[#EE9F12] duration-200 hover:translate-y-4 animate-bounce" alt="">
              </div>
            </div>
          </div>
          <div id="gamesSection" class="pb-4">
            <h1 class="text-center font-sans text-5xl font-bold text-white">
              {{ pageConfig?.games || "" }}
            </h1>
          </div>
        </div>
      </div>
    </div>

    <div class="animate-delay-[900ms] animate-fade-right overflow-hidden ">
      <div class="py-10 mx-4 lg:mx-20">
        <CardCarrossel/>
      </div>
    </div>
  </div>

  <!-- QUEM SOMOS -->
  <div id="quemSomosSection" class="bg-white py-12 mx-5 lg:mx-10">
    <div class="bg-white py-5 animate-delay-[1000ms] animate-fade-left">
      <div class="py-2 mx-5 rounded-md justify-center text-[#393955] lg:mx-52 md:flex md:items-center">
        <div class="max-w-screen-sm md:pr-20 ">
          <p class="mt-2 font-black md:mt-6 py-3 font-roboto text-4xl lg:text-5xl text-center md:text-start">
            {{ pageConfig?.aboutUs || "" }}
          </p>
          <p class="mt-3 text-center py-3 font-roboto md:text-lg text-md md:text-start margin">
            {{ pageConfig?.aboutUsContent || "" }}
          </p>
        </div>
        <div class="flex items-center justify-center shadow-none py-10 overflow-hidden">
          <img
              class="rounded-3xl"
              :src="getAboutUsImage"
              alt="Logo ou imagem Quem Somos"
          />
        </div>
      </div>
    </div>
  </div>
  <hr class="mx-5 lg:mx-10 border-gray-200 lg:my-2"/>

  <!-- CONTATO -->
  <div class="flex justify-center items-center py-10 mx-5 animate-delay-[1100ms] animate-fade-left">
    <div id="contatoSection" class="bg-whitepy-12">
      <h1 class="mb-4 text-center font-sans text-4xl lg:text-5xl font-bold text-black">
        {{ pageConfig?.contactUs || "" }}
      </h1>
      <!-- Vários contatos (e-mails, telefones, etc) -->
      <div v-if="contactList.length">
        <div v-for="(contact, idx) in contactList" :key="idx">
          <a v-if="isEmail(contact)" :href="'mailto:' + contact">
            <h1 class="text-2xl font-bold lg:text-4xl text-[#8348F2] duration-200 hover:-translate-y-4">
              {{ contact }}
            </h1>
          </a>
          <h1 v-else class="text-2xl font-bold lg:text-4xl text-[#8348F2]">
            {{ contact }}
          </h1>
        </div>
      </div>
    </div>
  </div>

  <hr class="mx-5 lg:mx-10 border-gray-200 lg:my-2"/>

  <!-- PARCEIROS -->
  <div class="bg-white py-12 mx-10 animate-delay-[1200ms] animate-fade-right">
    <h1 class="text-center font-sans text-base text-black">{{ pageConfig?.partnersTitle || "Parceiros" }}</h1>
    <Brands :brands="brandsList"/>
  </div>

  <div class="animate-delay-[1200ms] animate-fade-right">
    <Footer :language="language"/>
  </div>
</template>

<script setup>
import {ref, onMounted, watch, computed} from 'vue';
import {useRoute, useRouter} from 'vue-router';
import api from '@/services/api';
import Brands from "@/components/sitegame/Brands.vue";
import Navbar from "@/components/sitegame/Navbar.vue";
import Footer from "@/components/sitegame/Footer.vue";
import CardCarrossel from "@/components/sitegame/CardCarrossel.vue";

const homeType = "entreterimento";
const route = useRoute();
const router = useRouter();
const language = computed(() => route.path.startsWith('/en') ? 'en' : 'pt');

// Dados completos dos dois idiomas
const configPt = ref({});
const configEn = ref({});
const pageConfig = computed(() => {
  // Sempre retorna o objeto do idioma atual,
  // mas puxando as imagens SEMPRE do PT-BR
  if (language.value === 'en' && Object.keys(configEn.value).length) {
    // Merge: textos EN + imagens PT
    return {
      ...configEn.value,
      aboutUsImage: configPt.value.aboutUsImage,
      partnersList: configPt.value.partnersList,
      partnersListUpload: configPt.value.partnersListUpload,
    };
  }
  return configPt.value || {};
});

// Partners (sempre pega as imagens do PT-BR)
const brandsList = computed(() => {
  const cfg = configPt.value;
  if (Array.isArray(cfg?.partnersListUpload)) {
    return cfg.partnersListUpload
        .filter(Boolean)
        .map(img => ({ url: img, alt: 'Parceiro' }));
  }
  if (typeof cfg?.partnersList === 'string') {
    return cfg.partnersList
        .split(',')
        .map(s => s.trim())
        .filter(Boolean)
        .map(img => ({ url: img, alt: 'Parceiro' }));
  }
  return [];
});

const contactList = computed(() => {
  const cfg = language.value === 'en' ? configEn.value : configPt.value;
  if (cfg?.contacts && Array.isArray(cfg.contacts) && cfg.contacts.length) {
    return cfg.contacts.filter(Boolean);
  } else if (cfg?.emailAddress) {
    return cfg.emailAddress.split(',').map(e => e.trim()).filter(Boolean);
  }
  return [];
});
const isEmail = (contact) => contact.includes('@');

const getAboutUsImage = computed(() => {
  const img = configPt.value?.aboutUsImage;
  if (!img) return require('@/assets/logo.png');
  if (Array.isArray(img)) return img[0] || require('@/assets/logo.png');
  return img;
});

const fetchConfig = async () => {
  const res = await api.getHomeConfig(homeType);
  configPt.value = res.data.pt || {};
  configEn.value = res.data.en || {};
};

const navigateToSection = (id) => {
  const el = document.getElementById(id);
  if (el) {
    el.scrollIntoView({ behavior: 'smooth' });
  }
};

onMounted(fetchConfig);
watch(() => route.fullPath, fetchConfig);
</script>



